import { type NewProductDisplay } from '@/data/useNewProductDisplay'
import {
  ArtworkType,
  type BackgroundCommonFieldsFragment,
  type CollageCommonFieldsFragment,
  DesignGroupType,
  type StickerGroupCommonFieldsFragment,
} from '@/graphql/creator-platform/generated'
import { backendLocation, isLocalItem } from '@/graphql/utils'
import { usePricePlansStore } from '@/store/pricePlansStore'

import { toFullSizeImage } from '../designArea/utils'
import {
  type CollageDisplayCardContent,
  type DisplayCardContent,
  type MultiLayerPreviewBackgroundDisplayCardContent,
  type MultiLayersPreviewCollageDisplayCardContent,
  type SingleImagePreviewBackgroundDisplayCardContent,
  type SingleImagePreviewCollageDisplayCardContent,
  type StickerGroupDisplayCardContent,
} from './DisplayCard'

interface SingleImagePreviewOption {
  readonly type: 'single-image'
}
interface MultiLayerPreviewOption {
  readonly display: NewProductDisplay
  readonly type: 'multi-layer'
}
export type PreviewOption = MultiLayerPreviewOption | SingleImagePreviewOption

const pricePlanStore = usePricePlansStore()
export async function buildCollageDisplayCardContent(
  collage: CollageCommonFieldsFragment,
  option: SingleImagePreviewOption,
): Promise<null | SingleImagePreviewCollageDisplayCardContent>
export async function buildCollageDisplayCardContent(
  collage: CollageCommonFieldsFragment,
  option: MultiLayerPreviewOption,
): Promise<MultiLayersPreviewCollageDisplayCardContent | null>
export async function buildCollageDisplayCardContent(
  collage: CollageCommonFieldsFragment,
  option: PreviewOption,
): Promise<CollageDisplayCardContent | null> {
  const collagePrice = await pricePlanStore.calculateCollagePrice(collage)
  if (collagePrice == null) return null

  const imageUrl = collage.squareThumbnailUrl ?? collage.designUrl
  if (typeof imageUrl !== 'string') return null

  const common = {
    artworkType: ArtworkType.Collage,
    customizationLevel: collage.customizationLevel,
    displayId: collage.displayId,
    id: collage.id,
    subtitle:
      collage.creator.userI18ns.find((x) => x.location === backendLocation)?.name ??
      collage.creator.name,
    title: collage.collageI18ns.find((x) => x.location === backendLocation)?.name ?? collage.name,
  } as const satisfies Omit<DisplayCardContent, 'compareAtPrice' | 'preview' | 'price'>

  switch (option.type) {
    case 'multi-layer': {
      if (collage.designGroupType === DesignGroupType.General) return null
      const display = option.display
      return {
        ...common,
        compareAtPrice: collagePrice.compareAtPrice + display.compareAtPrice,
        price: collagePrice.price + display.price,
        preview: {
          type: 'multi-layer',
          design: toFullSizeImage(imageUrl),
          designRect: display.designRect,
          mask: display.previewMaskUrl,
          postDesignLayers: display.postDesignLayers,
          preDesignLayers: display.preDesignLayers,
        },
      }
    }
    case 'single-image': {
      return {
        ...common,
        compareAtPrice: collagePrice.compareAtPrice,
        price: collagePrice.price,
        preview: {
          imageUrl,
          type: 'single-image',
        },
      }
    }
  }
}

export async function buildBackgroundDisplayCardContent(
  background: BackgroundCommonFieldsFragment,
  option: SingleImagePreviewOption,
): Promise<null | SingleImagePreviewBackgroundDisplayCardContent>
export async function buildBackgroundDisplayCardContent(
  background: BackgroundCommonFieldsFragment,
  option: MultiLayerPreviewOption,
): Promise<MultiLayerPreviewBackgroundDisplayCardContent | null>
export async function buildBackgroundDisplayCardContent(
  background: BackgroundCommonFieldsFragment,
  option: PreviewOption,
): Promise<DisplayCardContent | null> {
  const sku = background.pricePlan?.sku ?? null
  if (sku === null) {
    return null
  }
  const backgroundPriceInfo = await pricePlanStore.getPrice(sku, background.id)
  const imageUrl = background.squareThumbnailUrl ?? background.imageUrl
  if (backgroundPriceInfo === null || typeof imageUrl !== 'string') return null

  const common = {
    artworkType: ArtworkType.Background,
    customizationLevel: background.customizationLevel,
    displayId: background.displayId,
    id: background.id,
    subtitle: background.creator.userI18ns.find(isLocalItem)?.name ?? background.creator.name,
    title: background.backgroundI18ns.find(isLocalItem)?.name ?? background.name,
  } as const satisfies Omit<DisplayCardContent, 'compareAtPrice' | 'preview' | 'price'>

  switch (option.type) {
    case 'multi-layer': {
      if (background.designGroupType === DesignGroupType.General) return null
      const display = option.display
      return {
        ...common,
        compareAtPrice: backgroundPriceInfo.compareAtPrice + display.compareAtPrice,
        price: backgroundPriceInfo.price + display.price,
        preview: {
          type: 'multi-layer',
          design: toFullSizeImage(imageUrl),
          designRect: display.designRect,
          mask: display.previewMaskUrl,
          postDesignLayers: display.postDesignLayers,
          preDesignLayers: display.preDesignLayers,
        },
      }
    }
    case 'single-image': {
      return {
        ...common,
        compareAtPrice: backgroundPriceInfo.compareAtPrice,
        price: backgroundPriceInfo.price,
        preview: {
          imageUrl,
          type: 'single-image',
        },
      }
    }
  }
}

export async function buildStickerGroupDisplayCardContent(
  stickerGroup: StickerGroupCommonFieldsFragment,
): Promise<null | StickerGroupDisplayCardContent> {
  const sku = stickerGroup.pricePlan?.sku ?? null
  if (sku === null) {
    return null
  }
  const priceInfo = await pricePlanStore.getPrice(sku, stickerGroup.id)
  if (priceInfo === null) return null
  return {
    artworkType: ArtworkType.StickerGroup,
    compareAtPrice: priceInfo.compareAtPrice,
    customizationLevel: stickerGroup.customizationLevel,
    displayId: stickerGroup.displayId,
    id: stickerGroup.id,
    preview: { imageUrls: stickerGroup.stickers.map((s) => s.imageUrl), type: 'multi-images' },
    price: priceInfo.price,
    subtitle: stickerGroup.creator.userI18ns.find(isLocalItem)?.name ?? stickerGroup.creator.name,
    title: stickerGroup.stickerGroupI18ns.find(isLocalItem)?.name ?? stickerGroup.name,
  }
}
